import CustomersTable from "@/components/customers/CustomersTable";
import { InviteUserDialog } from "@/components/InviteUserDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAdminCustomers, useAdminProjects } from "../-admin-context";

export const Route = createFileRoute("/dashboard/admin/customers/")({
  head: () => ({
    meta: [
      {
        title: "Customers - GetHandle Admin",
      },
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const customersResource = useAdminCustomers();
  const projectsResource = useAdminProjects();
  const [search, setSearch] = useQueryState("search", parseAsString.withDefault(""));

  const loading = customersResource.tag === "loading";
  const error = customersResource.tag === "failure" ? customersResource.error.message : null;
  const customers = customersResource.tag === "success" ? customersResource.asData : [];
  const projects = projectsResource.tag === "success" ? projectsResource.asData : [];

  // Local search state drives filtering instantly; URL is updated lazily
  const [localSearch, setLocalSearch] = useState(search);
  const urlDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state if URL search changes externally (e.g. back/forward navigation)
  const prevUrlSearch = useRef(search);
  useEffect(() => {
    if (search !== prevUrlSearch.current) {
      prevUrlSearch.current = search;
      setLocalSearch(search);
    }
  }, [search]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (urlDebounceTimer.current) clearTimeout(urlDebounceTimer.current);
    };
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
    if (urlDebounceTimer.current) clearTimeout(urlDebounceTimer.current);
    urlDebounceTimer.current = setTimeout(() => {
      setSearch(value);
    }, 300);
  }, [setSearch]);

  if (loading) {
    return <div className="container mx-auto p-1 sm:p-6">Loading customers...</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto p-1 sm:p-6">
        <Alert variant="destructive">
          <AlertDescription>Error loading customers: {error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-4 p-1 sm:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <h2 className="text-2xl font-bold tracking-tight">Customers ({customers.length})</h2>

        <div className="flex-1" />

        <InviteUserDialog defaultRoles={["customer"]} defaultRole="customer" />

        <div className="w-2" />

        <Link to="/dashboard/admin/customers/create" className="sm:ml-auto">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Customer
          </Button>
        </Link>
      </div>

      <CustomersTable
        customers={customers}
        projects={projects}
        loading={loading}
        searchValue={localSearch}
        onSearchChange={handleSearchChange}
      />
    </div>
  );
}
